import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WorkOrder } from '@/types'

interface WorkOrderState {
  workOrders: WorkOrder[]
  selectedWorkOrder: WorkOrder | null
  isLoading: boolean
  error: string | null
  lastUpdated: string | null
  statusCounts: {
    pending: number
    assigned: number
    'in-progress': number
    'on-hold': number
    completed: number
    cancelled: number
  }
  overdueCount: number
}

const initialState: WorkOrderState = {
  workOrders: [],
  selectedWorkOrder: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  statusCounts: {
    pending: 0,
    assigned: 0,
    'in-progress': 0,
    'on-hold': 0,
    completed: 0,
    cancelled: 0,
  },
  overdueCount: 0,
}

const workOrderSlice = createSlice({
  name: 'workOrders',
  initialState,
  reducers: {
    setWorkOrders: (state, action: PayloadAction<WorkOrder[]>) => {
      state.workOrders = action.payload
      state.lastUpdated = new Date().toISOString()
      state.error = null
      
      // Update counts
      state.statusCounts = action.payload.reduce(
        (counts, workOrder) => {
          counts[workOrder.status]++
          return counts
        },
        { pending: 0, assigned: 0, 'in-progress': 0, 'on-hold': 0, completed: 0, cancelled: 0 }
      )
      
      // Count overdue work orders
      const now = new Date()
      state.overdueCount = action.payload.filter(wo => {
        const dueDate = new Date(wo.dueDate)
        return dueDate < now && !['completed', 'cancelled'].includes(wo.status)
      }).length
    },
    addWorkOrder: (state, action: PayloadAction<WorkOrder>) => {
      state.workOrders.unshift(action.payload)
      state.statusCounts[action.payload.status]++
      
      // Check if overdue
      const dueDate = new Date(action.payload.dueDate)
      const now = new Date()
      if (dueDate < now && !['completed', 'cancelled'].includes(action.payload.status)) {
        state.overdueCount++
      }
      
      state.lastUpdated = new Date().toISOString()
    },
    updateWorkOrder: (state, action: PayloadAction<{ id: string; updates: Partial<WorkOrder> }>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.id)
      if (index !== -1) {
        const oldWorkOrder = state.workOrders[index]
        const updatedWorkOrder = { ...oldWorkOrder, ...action.payload.updates }
        state.workOrders[index] = updatedWorkOrder
        
        // Update status counts if status changed
        if (oldWorkOrder.status !== updatedWorkOrder.status) {
          state.statusCounts[oldWorkOrder.status]--
          state.statusCounts[updatedWorkOrder.status]++
        }
        
        // Update overdue count
        const now = new Date()
        const oldDueDate = new Date(oldWorkOrder.dueDate)
        const newDueDate = new Date(updatedWorkOrder.dueDate)
        
        const wasOverdue = oldDueDate < now && !['completed', 'cancelled'].includes(oldWorkOrder.status)
        const isOverdue = newDueDate < now && !['completed', 'cancelled'].includes(updatedWorkOrder.status)
        
        if (wasOverdue && !isOverdue) {
          state.overdueCount = Math.max(0, state.overdueCount - 1)
        } else if (!wasOverdue && isOverdue) {
          state.overdueCount++
        }
        
        // Update selected work order if it's the one being updated
        if (state.selectedWorkOrder?.id === action.payload.id) {
          state.selectedWorkOrder = updatedWorkOrder
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    removeWorkOrder: (state, action: PayloadAction<string>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload)
      if (index !== -1) {
        const workOrder = state.workOrders[index]
        state.workOrders.splice(index, 1)
        
        state.statusCounts[workOrder.status]--
        
        // Update overdue count
        const dueDate = new Date(workOrder.dueDate)
        const now = new Date()
        if (dueDate < now && !['completed', 'cancelled'].includes(workOrder.status)) {
          state.overdueCount = Math.max(0, state.overdueCount - 1)
        }
        
        if (state.selectedWorkOrder?.id === action.payload) {
          state.selectedWorkOrder = null
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    setSelectedWorkOrder: (state, action: PayloadAction<WorkOrder | null>) => {
      state.selectedWorkOrder = action.payload
    },
    assignWorkOrder: (state, action: PayloadAction<{ 
      id: string 
      assignedTo: WorkOrder['assignedTo']
    }>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.id)
      if (index !== -1) {
        const oldStatus = state.workOrders[index].status
        state.workOrders[index] = {
          ...state.workOrders[index],
          assignedTo: action.payload.assignedTo,
          status: 'assigned',
        }
        
        // Update status counts
        if (oldStatus !== 'assigned') {
          state.statusCounts[oldStatus]--
          state.statusCounts.assigned++
        }
        
        if (state.selectedWorkOrder?.id === action.payload.id) {
          state.selectedWorkOrder = state.workOrders[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    updateWorkOrderProgress: (state, action: PayloadAction<{
      id: string
      progress: number
      actualHours?: number
      notes?: string
    }>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.id)
      if (index !== -1) {
        const updates: Partial<WorkOrder> = {
          progress: action.payload.progress,
        }
        
        if (action.payload.actualHours !== undefined) {
          updates.actualHours = action.payload.actualHours
        }
        
        // Determine status based on progress
        if (action.payload.progress >= 100) {
          const oldStatus = state.workOrders[index].status
          updates.status = 'completed'
          updates.actualEnd = new Date().toISOString()
          
          // Update status counts
          if (oldStatus !== 'completed') {
            state.statusCounts[oldStatus]--
            state.statusCounts.completed++
          }
          
          // Remove from overdue count if it was overdue
          const dueDate = new Date(state.workOrders[index].dueDate)
          const now = new Date()
          if (dueDate < now) {
            state.overdueCount = Math.max(0, state.overdueCount - 1)
          }
        } else if (state.workOrders[index].status === 'assigned') {
          // Start work order if it was just assigned
          const oldStatus = state.workOrders[index].status
          updates.status = 'in-progress'
          updates.actualStart = new Date().toISOString()
          
          state.statusCounts[oldStatus]--
          state.statusCounts['in-progress']++
        }
        
        state.workOrders[index] = { ...state.workOrders[index], ...updates }
        
        if (state.selectedWorkOrder?.id === action.payload.id) {
          state.selectedWorkOrder = state.workOrders[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    addWorkOrderUpdate: (state, action: PayloadAction<{
      workOrderId: string
      update: WorkOrder['updates'][0]
    }>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.workOrderId)
      if (index !== -1) {
        state.workOrders[index].updates.push(action.payload.update)
        
        if (state.selectedWorkOrder?.id === action.payload.workOrderId) {
          state.selectedWorkOrder = state.workOrders[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      if (action.payload) {
        state.error = null
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const {
  setWorkOrders,
  addWorkOrder,
  updateWorkOrder,
  removeWorkOrder,
  setSelectedWorkOrder,
  assignWorkOrder,
  updateWorkOrderProgress,
  addWorkOrderUpdate,
  setLoading,
  setError,
  clearError,
} = workOrderSlice.actions

// Selectors
export const selectWorkOrders = (state: { workOrders: WorkOrderState }) => state.workOrders.workOrders
export const selectSelectedWorkOrder = (state: { workOrders: WorkOrderState }) => state.workOrders.selectedWorkOrder
export const selectWorkOrdersLoading = (state: { workOrders: WorkOrderState }) => state.workOrders.isLoading
export const selectWorkOrdersError = (state: { workOrders: WorkOrderState }) => state.workOrders.error
export const selectWorkOrderStatusCounts = (state: { workOrders: WorkOrderState }) => state.workOrders.statusCounts
export const selectOverdueWorkOrdersCount = (state: { workOrders: WorkOrderState }) => state.workOrders.overdueCount
export const selectWorkOrdersLastUpdated = (state: { workOrders: WorkOrderState }) => state.workOrders.lastUpdated

// Complex selectors
export const selectWorkOrdersByStatus = (status: WorkOrder['status']) => (state: { workOrders: WorkOrderState }) =>
  state.workOrders.workOrders.filter(wo => wo.status === status)

export const selectActiveWorkOrders = (state: { workOrders: WorkOrderState }) =>
  state.workOrders.workOrders.filter(wo => !['completed', 'cancelled'].includes(wo.status))

export const selectOverdueWorkOrders = (state: { workOrders: WorkOrderState }) => {
  const now = new Date()
  return state.workOrders.workOrders.filter(wo => {
    const dueDate = new Date(wo.dueDate)
    return dueDate < now && !['completed', 'cancelled'].includes(wo.status)
  })
}

export const selectWorkOrdersByAssignee = (assigneeId: string) => (state: { workOrders: WorkOrderState }) =>
  state.workOrders.workOrders.filter(wo => wo.assignedTo?.id === assigneeId)

export const selectWorkOrdersByEquipment = (equipmentId: string) => (state: { workOrders: WorkOrderState }) =>
  state.workOrders.workOrders.filter(wo => wo.equipmentId === equipmentId)

export default workOrderSlice.reducer